import {
    ContextMenuContext,
    DragElement,
    classNames,
    dispatchCustomEvent,
    documentObject,
    getButton,
    getDragState,
    getKey,
    logger,
    useDrag,
    useEvent,
    useStorage,
    windowObject,
} from '@fold-dev/core'
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
    FOLD_TODO_DRAG_TASK_CACHE,
    FOLD_TODO_SHIFT_FLAG,
    RICH_EDITOR_FOCUS_DELAY,
    TodoContext,
    TodoTaskEditor,
    TodoTaskItem,
    TodoTasks,
    TodoTypes,
    getCollapsedStateFromLocalStorage,
    setMidnight,
    useTodoEvent,
} from '../'
import { getCustomGhostElement, stopEvent } from '../../helpers'

export const TASK_COLLAPSED = 'TASK_COLLAPSED'

const findSubtaskAmount = (tasks = []) => {
    let subtasks = 0

    const st = (tasks = []) => {
        tasks.map(({ tasks = [] }) => {
            subtasks++
            if (!!tasks.length) st(tasks)
        })
    }

    st(tasks)

    return subtasks
}

export type TodoTaskProps = {
    task: TodoTypes.Task
    visible: boolean
    freeze: boolean
    indent: number
    parentSelected?: boolean
}

export const TodoTask = (props: TodoTaskProps) => {
    const { task, visible = true, freeze, parentSelected } = props
    const {
        id,
        title,
        description,
        parentTaskId,
        complete,
        read,
        points,
        complexity,
        color,
        priority,
        type,
        editable,
        start,
        end,
        repeat,
        wholeDay,
        externalEmail,
        users = [],
        labels = [],
        badges = [],
        attachments = 0,
        comments = 0,
        teamId,
        projectId,
        projectName,
        projectColor,
        childTaskCount,
        // DEBUG
        position,
    } = task as any
    const {
        selection,
        setSelection,
        onTaskOpen,
        onTaskTitleUpdate,
        onTaskLabelAdd,
        onTaskLabelDelete,
        onTaskDateUpdate,
        onTaskUserAdd,
        onTaskUserDelete,
        onTaskComplete,
        onTaskAddBelow,
        onTaskAddAbove,
        noProjectPrefix,
        noTaskLabels,
        noTaskUsers,
        noTaskDates,
        noSelection,
        noIndent,
        kanbanMode,
        showProjectName,
        fields,
    } = useContext(TodoContext)
    const [taskTitle, setTaskTitle] = useState(title)
    const elementRef = useRef(null)
    const [addBelow, setAddBelow] = useState(task.addBelow)
    const [addAbove, setAddAbove] = useState(task.addAbove)
    const { getStorage, setStorage } = useStorage()
    const { setMenu } = useContext(ContextMenuContext)
    const [collapsed, setCollapsed] = useState(getCollapsedStateFromLocalStorage(TASK_COLLAPSED, id))
    const hasSubtasks = task.tasks ? !!task.tasks.length : false
    const {
        origin: { elementId },
    } = getDragState('origin')
    const [indent, setIndent] = useState(props.indent || 0)
    const [indentCache, _] = useState(props.indent || 0)
    const isSubtasksVisible = useMemo(
        () => hasSubtasks && !collapsed && elementId != id && visible && !kanbanMode,
        [hasSubtasks, collapsed, elementId, id, visible, kanbanMode]
    )
    const freezeSubtasks = elementId == id || freeze
    const {
        getNextIndent,
        getNextOutdent,
        setCustomGhostElement,
        setCustomGhostElementRotation,
        lockDrag,
        getGhostElement,
    } = useDrag()
    const selectTimer = useRef(null)
    const blockClickEvent = useRef(null)
    const [edit, setEdit] = useState(!!editable)
    const noDrag = edit ? true : undefined
    const [indentedEditor, setIndentedTaskBelow] = useState(false)
    const [canIndent, setCanIndent] = useState(true)
    const selected = useMemo(() => selection[id], [selection])
    //const subtaskAmount = useMemo(() => (task.tasks?.length && !isSubtasksVisible ? task.tasks?.length : 0), [isSubtasksVisible, task.tasks])
    const subtaskAmount = useMemo(() => findSubtaskAmount(task.tasks) || 0, [task?.tasks])
    const visibleFields = useMemo(
        () => ({
            assigned: fields?.assigned == undefined ? true : fields?.assigned,
            labels: fields?.labels == undefined ? true : fields?.labels,
            dates: fields?.dates == undefined ? true : fields?.dates,
        }),
        [fields]
    )
    const className = classNames({
        'kanban-mode': kanbanMode,
        'is-frozen': freeze,
    })

    // this function goes through the selection subtasks
    // and removes the selected subtask from the top level selection
    const recursivelyRemoveSubtaskSelections = (s) => {
        const selection = { ...s }
        const keyList = Object.keys(s)
        Object.keys(s).map((key) => {
            const findTask = (tasks = []) => {
                tasks.map(({ id, tasks }) => {
                    if (keyList.includes(id)) delete selection[id]
                    if (!!tasks) findTask(tasks)
                })
            }
            findTask(s[key].task.tasks)
        })
        return selection
    }

    const getSiblings = () => {
        const element = documentObject.getElementById(id)
        const { nextSibling, previousSibling } = element
        const previous = previousSibling ? (previousSibling.dataset.dragelement ? previousSibling : null) : null
        const previousIndent = previousSibling ? +previousSibling.dataset.indent : 0
        const next = nextSibling ? (nextSibling.dataset.dragelement ? nextSibling : null) : null
        const nextIndent = nextSibling ? +nextSibling.dataset.indent : 0

        return {
            previous,
            previousIndent,
            next,
            nextIndent,
        }
    }

    const handleIndent = () => {
        if (noIndent) return
        const { previous, previousIndent, next, nextIndent } = getSiblings()
        setIndent(getNextIndent({ indent, previous, previousIndent, next, nextIndent }))
    }

    const handleOutdent = () => {
        if (noIndent) return
        const { previous, previousIndent, next, nextIndent } = getSiblings()
        setIndent(getNextOutdent({ indent, previous, previousIndent, next, nextIndent }))
    }

    const handleAddAboveSave = ({ title, meta }) => {
        const el = documentObject.getElementById(id)
        const index = +el.dataset.index
        const areaId = el.dataset.areaid

        setAddAbove(false)
        setEdit(false)
        lockDrag(false)

        onTaskAddAbove({
            areaId,
            taskId: id,
            taskIndex: index,
            taskIndent: indent,
            title,
        })
    }

    const handleAddBelowSave = ({ title, meta }) => {
        const el = documentObject.getElementById(id)
        const index = +el.dataset.index
        const areaId = el.dataset.areaid

        setAddBelow(false)
        setEdit(false)
        lockDrag(false)

        onTaskAddBelow({
            areaId,
            taskId: id,
            taskIndex: index,
            taskIndent: indent,
            shouldIndent: indentedEditor,
            title,
        })
    }

    const handleAddAboveCancel = () => {
        setAddAbove(false)
    }

    const handleAddBelowCancel = () => {
        setAddBelow(false)
    }

    const handleMouseUp = (e) => {
        clearTimeout(selectTimer.current)
    }

    const handleEditOpen = ({ detail }) => {
        if (id == detail.id) {
            setEdit(true)
            lockDrag(true)
        }
    }

    const handleAddAboveOpen = ({ detail }) => {
        if (id == detail.id) setAddAbove(true)
    }

    const handleAddBelowOpen = ({ detail }) => {
        if (id == detail.id) setAddBelow(true)
    }

    const handleKeyDown = (e) => {
        const { isEscape, isShift } = getKey(e)

        if (isEscape) {
            blockClickEvent.current = true
            // TODO: do this better
            // the click event keeps the detail from opening once the user
            // hits escape and is hovering over the same card
            // 500 seems like the sweet spots in terms of user interaction speed
            setTimeout(() => (blockClickEvent.current = false), 500)
        }
    }

    const handleMouseDown = (e) => {
        windowObject[FOLD_TODO_DRAG_TASK_CACHE] = { ...task, indent: props.indent }

        if (hasSubtasks) {
            const badge: HTMLElement = document.createElement('span')
            badge.innerText = String(subtaskAmount + 1)
            badge.className = 'f-badge is-danger is-top-left f-row'
            badge.style.flexShrink = '0'
            badge.style.minWidth = '20px'
            badge.style.textAlign = 'center'
            badge.style.position = 'relative'
            //badge.style.left = '0px'

            const el: HTMLElement = getGhostElement()

            setTimeout(() => el.appendChild(badge), 500)
        }

        if (selection[id]) {
            setCustomGhostElementRotation('0deg')
            setCustomGhostElement(getCustomGhostElement(title, Object.keys(selection).length))
        }
    }

    const handleClick = (e) => {
        if (blockClickEvent.current) return

        const select = noSelection ? false : windowObject[FOLD_TODO_SHIFT_FLAG]
        const selectable = noSelection ? false : select && !parentSelected

        if (edit) {
            stopEvent(e)
            return
        }

        // if the select/shift button is active
        // and the task is not locked
        if (selectable) {
            e.preventDefault()
            e.stopPropagation()
            const newSelected = { ...selection }
            if (newSelected[id]) {
                delete newSelected[id]
            } else {
                newSelected[id] = { task }
            }
            setSelection(recursivelyRemoveSubtaskSelections(newSelected))
            return
        }

        if (select && parentSelected) {
            return
        }

        onTaskOpen({
            id,
            title,
            description,
            color,
            priority,
            type,
            complete,
            read,
            points,
            complexity,
            collapsed,
            parentTaskId,
            start,
            end,
            repeat,
            wholeDay,
            externalEmail,
            attachments,
            comments,
            users,
            labels,
            badges,
            teamId,
            projectId,
            projectName,
            projectColor,
            childTaskCount,
        })
    }

    const handleEdit = () => {
        setEdit(true)
        lockDrag(true)
    }

    const handleEditCancel = () => {
        setEdit(false)
        lockDrag(false)
    }

    const handleEditSave = (task) => {
        const { title } = task
        const el = documentObject.getElementById(id)
        const index = +el.dataset.index
        const areaId = el.dataset.areaid

        // optimistically update this first
        // so there is no flicker between the old and then when it updates from the API
        setTaskTitle(title)

        // update the API
        onTaskTitleUpdate({
            id,
            title,
            users,
            labels,
            index,
            areaId,
            indent: indentCache != indent ? indent : undefined,
        })

        setTimeout(() => {
            setEdit(false)
            lockDrag(false)
        }, RICH_EDITOR_FOCUS_DELAY)
    }

    const handleLabelAdd = (label) => {
        onTaskLabelAdd({ id, label })
    }

    const handleLabelDelete = (label) => {
        onTaskLabelDelete({ id, label })
    }

    const handleUserAdd = (user) => {
        onTaskUserAdd({ id, user })
    }

    const handleUserDelete = (user) => {
        onTaskUserDelete({ id, user })
    }

    const handleDateChange = ({ start, end, repeat, wholeDay }) => {
        onTaskDateUpdate({
            id,
            start,
            end,
            repeat,
            wholeDay,
        })
    }

    const handleComplete = () => {
        onTaskComplete({
            id,
            complete: !complete,
            start,
            end,
            repeat,
            wholeDay,
        })

        if (type == 'milestone' && !complete) dispatchCustomEvent('celebrate')
    }

    const handleCollapse = (e) => {
        stopEvent(e)

        const updatedCollapsed = !collapsed
        const sectionsCollapsed = JSON.parse(getStorage(TASK_COLLAPSED) || '{}')

        if (updatedCollapsed) {
            sectionsCollapsed[id] = true
        } else {
            delete sectionsCollapsed[id]
        }

        setStorage(TASK_COLLAPSED, JSON.stringify(sectionsCollapsed))
        setCollapsed(updatedCollapsed)
    }

    const handleContextMenuClick = (e) => {
        if (edit) return

        stopEvent(e)
        setMenu(
            {
                payload: {
                    id,
                    title,
                    description,
                    color,
                    priority,
                    type,
                    complete,
                    read,
                    points,
                    complexity,
                    collapsed,
                    editable,
                    parentTaskId,
                    start,
                    end,
                    repeat,
                    wholeDay,
                    externalEmail,
                    attachments,
                    comments,
                    users,
                    labels,
                    badges,
                    teamId,
                    tasks: task.tasks,
                    projectId,
                    projectName,
                    projectColor,
                    childTaskCount,
                },
            },
            { x: e.clientX, y: e.clientY }
        )
    }

    const handleDragStart = (e) => {
        const dates = {
            start: start ? new Date(start) : null,
            end: end ? new Date(end) : null,
            wholeDay: !start && !end ? true : wholeDay,
        }

        if (!!dates.start && !!dates.end && dates.start.getTime() >= dates.end.getTime()) {
            dates.end = new Date(dates.start.getTime() + 60 * 60 * 1000)
        }

        dispatchCustomEvent('tasks-to-calendar', {
            id,
            title,
            description,
            position,
            indent,
            color,
            repeat,
            users,
            ...dates,
        })
    }

    useEvent('mouseup', handleMouseUp)
    useEvent('keydown', handleKeyDown)

    useTodoEvent('edit-task', handleEditOpen)
    useTodoEvent('add-task-above', handleAddAboveOpen)
    useTodoEvent('add-task-below', handleAddBelowOpen)

    useLayoutEffect(() => {
        const el: any = elementRef.current
        if (el) el.addEventListener('contextmenu', handleContextMenuClick)
        return () => (el ? el.removeEventListener('contextmenu', handleContextMenuClick) : null)
    }, [id, priority, type, start, end, repeat, wholeDay])

    useLayoutEffect(() => {
        setTaskTitle(title)
    }, [title])

    useEffect(() => {
        if (isSubtasksVisible) {
            setIndentedTaskBelow(true)
            setCanIndent(false)
        } else {
            setCanIndent(true)
        }
    }, [isSubtasksVisible])

    return (
        <>
            <DragElement
                id={id}
                data-id={id}
                data-parent={parentTaskId}
                noDrag={noDrag}
                indent={indent}
                noIndent
                ref={elementRef}
                //onClick={handleClick}
                onMouseDown={handleDragStart}
                style={{ display: visible ? 'block' : 'none' }}
                className={className}>
                {addAbove && (
                    <div className="f-todo-task__add-below f-row is-indent">
                        <TodoTaskEditor
                            isAddAbove
                            id={id}
                            title=""
                            indented={false}
                            onAddBelowClose={handleAddAboveCancel}
                            onIndent={() => null}
                            onOutdent={() => null}
                            onSave={handleAddAboveSave}
                            onDismiss={handleAddAboveCancel}
                        />
                    </div>
                )}

                <TodoTaskItem
                    key={id}
                    // task state
                    // missing addBelow because we only need it
                    // in the current component
                    id={id}
                    position={position}
                    title={taskTitle}
                    //title={taskTitle + ' → ' + position}
                    description={description}
                    color={color}
                    priority={priority}
                    type={type}
                    complete={complete}
                    read={read}
                    points={+points}
                    complexity={complexity}
                    parentTaskId={parentTaskId}
                    collapsed={collapsed}
                    editable={editable}
                    start={start}
                    end={end}
                    repeat={repeat}
                    wholeDay={wholeDay}
                    externalEmail={externalEmail}
                    users={users || []}
                    labels={labels || []}
                    badges={badges || []}
                    attachments={attachments}
                    comments={comments}
                    teamId={teamId}
                    projectId={projectId}
                    projectName={noProjectPrefix ? undefined : projectName}
                    projectColor={noProjectPrefix ? undefined : projectColor}
                    childTaskCount={childTaskCount}
                    kanbanMode={kanbanMode}
                    showProjectName={showProjectName}
                    // general state
                    edit={edit}
                    parentSelected={parentSelected}
                    selected={selected}
                    showCollapse={hasSubtasks}
                    subtaskAmount={subtaskAmount}
                    visibleFields={visibleFields}
                    // event handlers
                    onEdit={handleEdit}
                    onEditSave={handleEditSave}
                    onEditCancel={handleEditCancel}
                    onComplete={handleComplete}
                    onCollapse={handleCollapse}
                    onIndent={handleIndent}
                    onOutdent={handleOutdent}
                    onMouseDown={handleMouseDown}
                    // toolbar buttons
                    onOpen={handleClick}
                    onLabelAdd={handleLabelAdd}
                    onLabelDelete={handleLabelDelete}
                    onUserAdd={handleUserAdd}
                    onUserDelete={handleUserDelete}
                    onDateChange={handleDateChange}
                    // from context
                    noTaskLabels={noTaskLabels}
                    noTaskDates={noTaskDates}
                    noTaskUsers={noTaskUsers}
                />

                {addBelow && (
                    <div className="f-todo-task__add-below f-row is-indent">
                        <TodoTaskEditor
                            isAddBelow
                            id={id}
                            title=""
                            indented={indentedEditor}
                            onAddBelowClose={handleAddBelowCancel}
                            onIndent={() => canIndent && setIndentedTaskBelow(true)}
                            onOutdent={() => canIndent && setIndentedTaskBelow(false)}
                            onSave={handleAddBelowSave}
                            onDismiss={handleAddBelowCancel}
                        />
                    </div>
                )}
            </DragElement>

            {isSubtasksVisible && (
                <TodoTasks
                    indent={indent + 1}
                    tasks={task.tasks}
                    visible={isSubtasksVisible}
                    freeze={freezeSubtasks}
                    parentSelected={selected || parentSelected}
                    parentColor={!!color ? color : undefined}
                />
            )}
        </>
    )
}
